<template>
  <div
    class="slide-wrapper"
    :class="{ show: showCollection }"
    @click="clickWrapper"
  >
    <div class="title">
      <slot name="title">
        <div class="title-desc">
          <div class="text-title">
            精选集：
          </div>
          <div class="marquee-wrapper">
            <marquee :animate="showCollection">
              {{ collectionInfo?.name }}
            </marquee>
          </div>
          <div
            class="follow-collection"
            @click.stop="followCollection"
          />
        </div>
      </slot>
    </div>
    <div class="line" />
    <div
      ref="wrapperRef"
      class="collection-wrapper"
    >
      <div class="collection">
        <div
          v-for="(item, index) in feedsList"
          :key="item.id"
          ref="collectItem"
          class="collection-item"
          @click.stop="clickCollectionItem(item, index)"
        >
          <div :class="{ active: activeIndex === index }">
            <div
              class="bg"
              :style="{ backgroundImage: getImage(item) }"
            >
              <div
                v-if="!!getCollectionIndex(item)"
                class="filter-top"
              >
                {{ getCollectionIndex(item) }}
              </div>
              <div class="filter">
                <div class="feed-desc">
                  {{ item.feedDesc }}
                </div>
                <div class="play-num">
                  <i /><span>{{ getPlayNum(item) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="collection-item check-last"
          @click.stop="clickCheckLast"
        >
          <div class="check-detail">
            {{ lastItemTxt }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import { useStore } from '@/store/index';
import BScroll, { BScrollInstance } from '@better-scroll/core';
import { ModelMetaFeed } from '@/types/model';
import { report } from '@/libs/report';
import { fixHttps } from '@/utils/no-import-meta/url';
import { addNumUint } from '@/utils/number';
import { IS_SSR } from '@/utils/env';
import Marquee from '@/components/common/marquee.vue';

const emits = defineEmits([
  'on-click-collectionItem',
  'on-follow-collection',
  'on-click-checkLastCollection',
  'on-click-old-collection-wrapper',
]);

// state
const { state } = useStore();
const showCollection = computed(() => state.showCollection);
const feedsList = computed(() => state.feedsList);
const activeIndex = computed(() => state.activeIndex);
const collectionInfo = computed(() => state.collectionInfo);

// data
const scroll = ref<null | BScrollInstance>(null);
const collectItems = ref<HTMLElement[]>([]);
const wrapperRef = ref<null | HTMLElement>(null);

// computed
const lastItemTxt = computed(() => {
  const feedNum = collectionInfo.value?.feedNum || 0;
  if (feedNum > 10) {
    return `查看剩余${feedNum - feedsList.value.length}个视频`;
  }
  return '查看更多视频';
});

// methods
const getPlayNum = (item: ModelMetaFeed) => addNumUint(item.playNum, 'cn');
const getImage = (feed: ModelMetaFeed) => `url(${fixHttps(feed?.images?.[0]?.url)})`;
const getCollectionIndex = (feed: ModelMetaFeed) => feed?.externInfo?.mpEx?.collectionIndex;
const scrollToElement = (tempIndex: number, speed = 300) => {
  if (!scroll.value) return;
  const index = Math.max(tempIndex, 1);
  scroll.value.scrollToElement(collectItems.value[index - 1], speed, false, false);
};
const followCollection = () => {
  emits('on-follow-collection');
};
const clickCollectionItem = (item: ModelMetaFeed, index: number) => {
  scrollToElement(index);
  emits('on-click-collectionItem', item, index);
};
const clickCheckLast = () => {
  emits('on-click-checkLastCollection');
};
const clickWrapper = () => {
  emits('on-click-old-collection-wrapper');
};

watch(activeIndex, (val) => {
  if (IS_SSR) return;
  if (val >= 0)  nextTick(() => scrollToElement(val));
});

onMounted(() => {
  if (!wrapperRef.value) return;
  // 缓存dom节点
  collectItems.value = wrapperRef.value.querySelectorAll('.collection-item') as unknown as HTMLElement[];
  scroll.value = new BScroll(wrapperRef.value, { click: true, scrollX: true });
  if (activeIndex.value >= 0) scrollToElement(activeIndex.value, 0);
  report('collection.back.exposure');
});

</script>

<style lang="scss" scoped>
.slide-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  @include responsiveStyleSingle(bottom, 14.9333,'vw');
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 12;
  transition: 0.3s ease;
  transform: translate3d(0, 150%, 0);
  &.show {
    transform: translate3d(0, 0, 0);
  }
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    @include responsiveStyleSingle(padding-left, 4.2667,'vw');
    @include responsiveStyleSingle(padding-right, 4.2667,'vw');
    font-weight: bold;
    @include responsiveStyleSingle(font-size, 4.8, 'vw');
    color: #ffffff;
    @include responsiveStyleSingle(line-height, 5.8667,'vw');
    .title-desc {
      display: flex;
      align-items: center;
    }
    .text-title {
      white-space: nowrap;
    }
    .follow-collection {
      @include responsiveStyleSingle(width, 4.8,'vw');
      @include responsiveStyleSingle(height, 4.8,'vw');
      @include responsiveStyleSingle(margin-left, 1.3333,'vw');
      border-radius: 50%;
      background-image: url(https://qzonestyle.gtimg.cn/aoi/sola/20200227200752_CdKjsgYZBZ.png);
      background-size: 100% 100%;
    }
    .marquee-wrapper {
      @include responsiveStyleSingle(max-width, 50.8,'vw');
    }
  }
  .line {
    height: 1px;
    width: 100%;
    @include responsiveStyleSingle(margin-bottom, 2.4,'vw');
    @include responsiveStyleSingle(margin-top, 1.3333,'vw');
    background: rgba(255, 255, 255, 0.2);
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
      transform: scaleY(0.5);
    }
    @media (-webkit-min-device-pixel-ratio: 3), (min-resolution: 3dppx) {
      transform: scaleY(0.333);
    }
  }
  .collection-wrapper {
    width: 100%;
    font-size: 0;
    .collection {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      @include responsiveStyleSingle(padding-left, 2.1333,'vw');
      @include responsiveStyleSingle(padding-right, 2.1333,'vw');
      font-size: 0;
      .collection-item {
        box-sizing: border-box;
        @include responsiveStyleSingle(width, 26.1333, 'vw');
        @include responsiveStyleSingle(padding-left, 2.1333,'vw');
        @include responsiveStyleSingle(height, 32, 'vw');
        * {
          box-sizing: inherit !important;
        }
        &:last-child {
          @include responsiveStyleSingle(margin-right, 2.1333, 'vw');
        }
        & > div {
          position: relative;
          width: 100%;
          height: 100%;
          @include responsiveStyleSingle(border-radius, 1.3333, 'vw');
          border-color: transparent;
          border-style: solid;
          @include responsiveStyleSingle(border-width, 0.4,'vw');
          &.active {
            border-color: #fff;
            border-style: solid;
            @include responsiveStyleSingle(border-width, 0.4,'vw');
            overflow: hidden;
          }
          .bg {
            position: absolute;
            @include responsiveStyleSingle(left, -0.4, 'vw');
            @include responsiveStyleSingle(top, -0.4, 'vw');
            @include responsiveStyleSingle(width, 24, 'vw');
            @include responsiveStyleSingle(height, 32, 'vw');
            background-size: cover;
            background-position: center center;
            @include responsiveStyleSingle(border-radius, 1.3333, 'vw');
            overflow: hidden;
          }
          .filter-top {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            @include responsiveStyleSingle(height, 8, 'vw');
            @include responsiveStyleSingle(padding-top, 1.3333, 'vw');
            @include responsiveStyleSingle(padding-right, 1.6, 'vw');
            background-image: linear-gradient(360deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
            color: #fff;
            @include responsiveStyleSingle(font-size, 2.9333, 'vw');
            font-weight: bold;
            @include responsiveStyleSingle(line-height, 4, 'vw');
          }
          .filter {
            position: absolute;
            left: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            width: 100%;
            // height: 100%;
            @include responsiveStyleSingle(height, 16, 'vw');
            @include responsiveStyleSingle(padding-right, 1.6, 'vw');
            @include responsiveStyleSingle(padding-bottom, 2, 'vw');
            @include responsiveStyleSingle(padding-left, 1.6, 'vw');
            background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 41%, rgba(0, 0, 0, 0.6) 100%);
            font-weight: bold;
            @include responsiveStyleSingle(font-size, 2.9333, 'vw');
            color: #ffffff;
            @include responsiveStyleSingle(line-height, 4, 'vw');
            .feed-desc {
              width: 100%;
              @include responsiveStyleSingle(margin-bottom, 0.2667, 'vw');
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .play-num {
              display: flex;
              align-items: center;
              i {
                @include responsiveStyleSingle(width, 3.2, 'vw');
                @include responsiveStyleSingle(height, 3.2, 'vw');
                @include responsiveStyleSingle(margin-right, 0.5333, 'vw');
                background-image: url(https://qzonestyle.gtimg.cn/aoi/sola/20200302151837_LoOzrHvdaG.png);
                background-size: 100% 100%;
              }
            }
          }
        }
        &.check-last {
          .check-detail {
            background: rgba(59, 59, 59, 0.8);
            color: #fff;
            height: 100%;
            @include responsiveStyleSingle(padding-left, 1.6,'vw');
            @include responsiveStyleSingle(padding-right, 1.6,'vw');
            @include responsiveStyleSingle(font-size, 2.9333, 'vw');
            font-weight: bold;
            white-space: normal;
            display: flex;
            align-items: center;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>
